import React, { forwardRef } from 'react'

interface InputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  label?: string
  error?: string
  helperText?: string
  variant?: 'default' | 'filled'
  size?: 'sm' | 'md' | 'lg'
  fullWidth?: boolean
}

const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      label,
      error,
      helperText,
      variant = 'default',
      size = 'md',
      fullWidth = false,
      className = '',
      ...props
    },
    ref
  ) => {
    const baseClasses =
      'border rounded-lg focus:ring-2 focus:ring-primary-400 focus:border-primary-400 transition-colors placeholder-neutral-400'

    const variantClasses = {
      default: 'border-neutral-300 bg-white',
      filled: 'border-neutral-300 bg-neutral-50',
    }

    const sizeClasses = {
      sm: 'px-2.5 py-1.5 text-sm',
      md: 'px-3 py-2 text-sm',
      lg: 'px-3.5 py-2.5 text-base',
    }

    const widthClass = fullWidth ? 'w-full' : ''
    const errorClass = error ? 'border-red-500 focus:ring-red-400 focus:border-red-500' : ''
    const inputClasses = `box-border ${baseClasses} ${variantClasses[variant]} ${sizeClasses[size]} ${widthClass} ${errorClass} ${className}`

    return (
      <div className={`${fullWidth ? 'w-full' : ''} space-y-1`}>
        {label && (
          <label className="block text-sm font-medium text-neutral-700">{label}</label>
        )}
        <input ref={ref} className={inputClasses} {...props} />
        {error && <p className="text-sm text-red-600">{error}</p>}
        {helperText && !error && <p className="text-sm text-neutral-500">{helperText}</p>}
      </div>
    )
  }
)

Input.displayName = 'Input'

export default Input
